import { useEffect, useRef, useState } from "react";
import styles from "./ThemeToggle.module.css";

type Theme = "popcorn" | "popcorn-warm" | "popcorn-night" | "popcorn-studio";

const STORAGE_KEY = "popcorn-ready-theme";
const DEFAULT_THEME: Theme = "popcorn";

const THEMES: { id: Theme; label: string }[] = [
  { id: "popcorn", label: "Accent" },
  { id: "popcorn-warm", label: "Warm" },
  { id: "popcorn-night", label: "Night" },
  { id: "popcorn-studio", label: "Studio" },
];

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

function parseTheme(value: string | null): Theme {
  if (value === "popcorn-ready") return DEFAULT_THEME;
  return THEMES.some((theme) => theme.id === value) ? (value as Theme) : DEFAULT_THEME;
}

/**
 * Theme switcher, rendered as a compact disclosure menu so it lives quietly in
 * a Settings area instead of four always-visible sidebar buttons. It still
 * flips `data-theme` on <html> + persists to localStorage, so every
 * CSS-variable theme restyles automatically.
 *
 * NOTE: whether these theme variants are user-facing or dev-only is an open
 * product question (see docs/scopes/studio-redesign-prs.md "Theme buttons").
 * They are placed in Settings here; gating them behind a dev/admin flag would
 * be the alternative.
 */
interface ThemeToggleProps {
  variant?: "menu" | "settings";
}

export default function ThemeToggle({ variant = "menu" }: ThemeToggleProps) {
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isSettingsVariant = variant === "settings";

  useEffect(() => {
    const initialTheme = parseTheme(window.localStorage.getItem(STORAGE_KEY));
    setTheme(initialTheme);
    applyTheme(initialTheme);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function selectTheme(nextTheme: Theme) {
    setTheme(nextTheme);
    applyTheme(nextTheme);
    window.localStorage.setItem(STORAGE_KEY, nextTheme);
    setOpen(false);
  }

  const activeLabel = THEMES.find((option) => option.id === theme)?.label ?? "Theme";
  const renderOptions = (role: "radio" | "menuitemradio") =>
    THEMES.map((option) => (
      <button
        key={option.id}
        type="button"
        role={role}
        aria-checked={theme === option.id}
        className={
          theme === option.id
            ? `${styles.option} ${styles.optionActive}`
            : styles.option
        }
        onClick={() => selectTheme(option.id)}
      >
        <span className={styles.optionDot} aria-hidden="true" />
        {option.label}
      </button>
    ));

  return (
    <div
      className={isSettingsVariant ? `${styles.root} ${styles.settings}` : styles.root}
      ref={rootRef}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.triggerLabel}>Theme</span>
        <span className={styles.triggerValue}>{activeLabel}</span>
      </button>
      {isSettingsVariant ? (
        <div className={styles.inlineOptions} role="radiogroup" aria-label="Theme options">
          {renderOptions("radio")}
        </div>
      ) : null}
      {open ? (
        <div className={styles.menu} role="menu" aria-label="Theme options">
          {renderOptions("menuitemradio")}
        </div>
      ) : null}
    </div>
  );
}
